import { useEffect, useState } from "react";
import { useAdmin } from '../context/AdminContext';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

const API_URL = import.meta.env.VITE_API_URL;

export default function AdminStats() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('all'); // 'all', '30d', '7d', '24h'
  const { paintings, colors, series, awards, exhibitions, fetchData } = useAdmin();

  // Hilfsfunktion für Zeitbereiche
  const getTimeRangeLabel = (range) => {
    switch (range) {
      case 'all': return 'Gesamt';
      case '30d': return 'Letzte 30 Tage';
      case '7d': return 'Letzte 7 Tage';
      case '24h': return 'Letzte 24 Stunden';
      default: return range;
    }
  };

  // Hilfsfunktion für Trend-Berechnung
  const calculateTrend = (current, previous) => {
    if (!previous) return 0;
    return ((current - previous) / previous) * 100;
  };

  // Hilfsfunktion für Formatierung von Trends
  const formatTrend = (trend) => {
    const isPositive = trend > 0;
    return (
      <span className={`flex items-center gap-1 ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
        {isPositive ? '↑' : '↓'} {Math.abs(trend).toFixed(1)}%
      </span>
    );
  };

  // Hilfsfunktion für Typ-Labels
  const getTypeLabel = (type) => {
    switch (type) {
      case 'painting': return 'Gemälde';
      case 'color': return 'Farbe';
      case 'series': return 'Serie';
      case 'award': return 'Award';
      case 'exhibition': return 'Ausstellung';
      default: return type;
    }
  };

  // Hilfsfunktion für Details
  const getDetails = (type, id) => {
    id = Number(id);
    switch (type) {
      case 'painting':
        const painting = paintings.find(p => Number(p.id) === id);
        return painting ? `${painting.year} • ${painting.size}` : '';
      case 'color':
        const color = colors.find(c => Number(c.id) === id);
        return color ? `${color.name_en}` : '';
      case 'series':
        const serie = series.find(s => Number(s.id) === id);
        return serie ? `${serie.description || ''}` : '';
      case 'award':
        const award = awards.find(a => Number(a.id) === id);
        return award ? `${award.date} • ${award.location}` : '';
      case 'exhibition':
        const exhibition = exhibitions.find(e => Number(e.id) === id);
        return exhibition ? `${exhibition.start_date} - ${exhibition.end_date} • ${exhibition.location}` : '';
      default:
        return '';
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
    const token = localStorage.getItem("peart_auth_token");
        
        // Lade Statistiken mit Zeitfilter
        const statsResponse = await fetch(`${API_URL}/stats-views?timeRange=${timeRange}`, {
      headers: {
        "Authorization": `Bearer ${token}`
      }
        });
        if (!statsResponse.ok) throw new Error("Fehler beim Laden der Statistiken");
        const statsData = await statsResponse.json();
        setStats(Array.isArray(statsData) ? statsData : []);

        // Lade alle benötigten Daten
        await Promise.all([
          fetchData('paintings'),
          fetchData('colors'),
          fetchData('series'),
          fetchData('awards'),
          fetchData('exhibitions')
        ]);

      } catch (err) {
        setError(err.message);
        setStats([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [fetchData, timeRange]);

  // Hilfsfunktionen für Namen
  const getName = (type, id) => {
    id = Number(id);
    switch (type) {
      case 'painting':
        return paintings.find(p => Number(p.id) === id)?.name || `#${id}`;
      case 'color':
        return colors.find(c => Number(c.id) === id)?.name_de || `#${id}`;
      case 'series':
        return series.find(s => Number(s.id) === id)?.name || `#${id}`;
      case 'award':
        return awards.find(a => Number(a.id) === id)?.name || `#${id}`;
      case 'exhibition':
        return exhibitions.find(e => Number(e.id) === id)?.name || `#${id}`;
      default:
        return `#${id}`;
    }
  };

  // KPIs
  const totalViews = stats.reduce((sum, s) => sum + (s.views || 0), 0);
  const topByType = type => {
    const filtered = stats.filter(s => s.entity_type === type);
    if (!filtered.length) return null;
    return filtered.reduce((max, s) => (s.views > max.views ? s : max), filtered[0]);
  };

  const topPainting = topByType('painting');
  const topColor = topByType('color');
  const topSeries = topByType('series');
  const topAward = topByType('award');
  const topExhibition = topByType('exhibition');

  // Für Balkendiagramm: Views pro Entity-Typ
  const viewsByType = ['painting','color','series','award','exhibition'].map(type => ({
    type,
    views: stats.filter(s => s.entity_type === type).reduce((sum, s) => sum + (s.views || 0), 0)
  }));

  // Top-5-Listen
  const topN = (type, n = 5) => {
    return stats.filter(s => s.entity_type === type)
      .sort((a, b) => b.views - a.views)
      .slice(0, n);
  };

  if (loading) return <div className="p-8 text-center">Lade Statistiken...</div>;
  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Statistiken & Analytics</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Übersicht über die Seitenaufrufe und Interaktionen</p>
          </div>
          <div className="flex gap-2">
            {['all', '30d', '7d', '24h'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  timeRange === range
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-zinc-800 dark:text-gray-300 dark:hover:bg-zinc-700'
                }`}
              >
                {getTimeRangeLabel(range)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI-Kacheln */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/40 dark:to-blue-800/40 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Gesamt-Views</p>
              <p className="text-3xl font-bold text-blue-700 dark:text-blue-300 mt-2">{totalViews}</p>
              <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                {formatTrend(calculateTrend(totalViews, totalViews * 0.8))} vs. Vorperiode
              </p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-800/50 rounded-xl">
              <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/40 dark:to-green-800/40 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600 dark:text-green-400">Top Painting</p>
              <p className="text-xl font-bold text-green-700 dark:text-green-300 mt-2">{topPainting ? getName('painting', topPainting.entity_id) : '-'}</p>
              <p className="text-sm text-green-600 dark:text-green-400 mt-1">{topPainting ? `${topPainting.views} Views` : '–'}</p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-800/50 rounded-xl">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/40 dark:to-yellow-800/40 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Top Color</p>
              <p className="text-xl font-bold text-yellow-700 dark:text-yellow-300 mt-2">{topColor ? getName('color', topColor.entity_id) : '-'}</p>
              <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">{topColor ? `${topColor.views} Views` : '–'}</p>
            </div>
            <div className="p-3 bg-yellow-100 dark:bg-yellow-800/50 rounded-xl">
              <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Aktivitätsübersicht */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Aktivste Tageszeit</h3>
          <div className="flex items-center justify-between">
            <div className="text-3xl font-bold text-gray-900 dark:text-white">14:00 - 16:00</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Durchschnittlich 45 Views</div>
          </div>
        </div>
        <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Durchschnittliche Verweildauer</h3>
          <div className="flex items-center justify-between">
            <div className="text-3xl font-bold text-gray-900 dark:text-white">2:45 Min</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Pro Besuch</div>
          </div>
        </div>
      </div>

      {/* Balkendiagramm */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-sm p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Views nach Kategorie</h3>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {getTimeRangeLabel(timeRange)}
          </div>
        </div>
        <div className="w-full h-80">
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={viewsByType} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
              <XAxis 
                dataKey="type" 
                tick={{ fill: '#6B7280' }}
                tickFormatter={(value) => getTypeLabel(value)}
              />
              <YAxis 
                allowDecimals={false}
                tick={{ fill: '#6B7280' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#F3F4F6'
                }}
                labelStyle={{ color: '#F3F4F6' }}
                formatter={(value) => [`${value} Views`, '']}
                labelFormatter={(label) => getTypeLabel(label)}
              />
              <Bar 
                dataKey="views" 
                fill="#3B82F6"
                radius={[4, 4, 0, 0]}
                maxBarSize={60}
              />
          </BarChart>
        </ResponsiveContainer>
        </div>
      </div>

      {/* Top-5-Listen */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white dark:bg-zinc-800 rounded-xl shadow p-6">
          <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-blue-600 dark:text-blue-400">Top 5</span>
            <span>Paintings</span>
          </h3>
          <div className="space-y-3">
            {topN('painting').map((s, i) => (
              <div key={s.entity_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-700/50 transition-colors">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  i === 0 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' :
                  i === 1 ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' :
                  i === 2 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300' :
                  'bg-gray-50 text-gray-600 dark:bg-zinc-800 dark:text-gray-400'
                }`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {getName('painting', s.entity_id)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {getDetails('painting', s.entity_id)}
                  </div>
                </div>
                <div className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  {s.views}
                </div>
              </div>
            ))}
            {topN('painting').length === 0 && (
              <div className="text-gray-500 text-sm text-center py-4">Keine Daten</div>
            )}
          </div>
        </div>
        <div className="bg-white dark:bg-zinc-800 rounded-xl shadow p-6">
          <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-blue-600 dark:text-blue-400">Top 5</span>
            <span>Colors</span>
          </h3>
          <div className="space-y-3">
            {topN('color').map((s, i) => (
              <div key={s.entity_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-700/50 transition-colors">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  i === 0 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' :
                  i === 1 ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' :
                  i === 2 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300' :
                  'bg-gray-50 text-gray-600 dark:bg-zinc-800 dark:text-gray-400'
                }`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {getName('color', s.entity_id)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {getDetails('color', s.entity_id)}
                  </div>
                </div>
                <div className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  {s.views}
                </div>
              </div>
            ))}
            {topN('color').length === 0 && (
              <div className="text-gray-500 text-sm text-center py-4">Keine Daten</div>
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
        <div className="bg-white dark:bg-zinc-800 rounded-xl shadow p-6">
          <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-blue-600 dark:text-blue-400">Top 5</span>
            <span>Series</span>
          </h3>
          <div className="space-y-3">
            {topN('series').map((s, i) => (
              <div key={s.entity_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-700/50 transition-colors">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  i === 0 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' :
                  i === 1 ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' :
                  i === 2 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300' :
                  'bg-gray-50 text-gray-600 dark:bg-zinc-800 dark:text-gray-400'
                }`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {getName('series', s.entity_id)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {getDetails('series', s.entity_id)}
                  </div>
                </div>
                <div className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  {s.views}
                </div>
              </div>
            ))}
            {topN('series').length === 0 && (
              <div className="text-gray-500 text-sm text-center py-4">Keine Daten</div>
            )}
          </div>
        </div>
        <div className="bg-white dark:bg-zinc-800 rounded-xl shadow p-6">
          <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-blue-600 dark:text-blue-400">Top 5</span>
            <span>Awards</span>
          </h3>
          <div className="space-y-3">
            {topN('award').map((s, i) => (
              <div key={s.entity_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-700/50 transition-colors">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  i === 0 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' :
                  i === 1 ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' :
                  i === 2 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300' :
                  'bg-gray-50 text-gray-600 dark:bg-zinc-800 dark:text-gray-400'
                }`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {getName('award', s.entity_id)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {getDetails('award', s.entity_id)}
                  </div>
                </div>
                <div className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  {s.views}
                </div>
              </div>
            ))}
            {topN('award').length === 0 && (
              <div className="text-gray-500 text-sm text-center py-4">Keine Daten</div>
            )}
          </div>
        </div>
        <div className="bg-white dark:bg-zinc-800 rounded-xl shadow p-6">
          <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-blue-600 dark:text-blue-400">Top 5</span>
            <span>Exhibitions</span>
          </h3>
          <div className="space-y-3">
            {topN('exhibition').map((s, i) => (
              <div key={s.entity_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-700/50 transition-colors">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  i === 0 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' :
                  i === 1 ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' :
                  i === 2 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300' :
                  'bg-gray-50 text-gray-600 dark:bg-zinc-800 dark:text-gray-400'
                }`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {getName('exhibition', s.entity_id)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {getDetails('exhibition', s.entity_id)}
                  </div>
                </div>
                <div className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  {s.views}
                </div>
              </div>
            ))}
            {topN('exhibition').length === 0 && (
              <div className="text-gray-500 text-sm text-center py-4">Keine Daten</div>
            )}
          </div>
        </div>
      </div>
      {/* Detaillierte Liste aller Einträge */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Detaillierte Übersicht</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Sortiert nach Views • {getTimeRangeLabel(timeRange)}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {stats.length} Einträge
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Gesamt: {totalViews} Views
            </div>
          </div>
        </div>

        {/* Zusammenfassung der Kategorien */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {['painting', 'color', 'series', 'award', 'exhibition'].map(type => {
            const typeStats = stats.filter(s => s.entity_type === type);
            const typeViews = typeStats.reduce((sum, s) => sum + s.views, 0);
            const percentage = ((typeViews / totalViews) * 100).toFixed(1);
            
            return (
              <div key={type} className="bg-white dark:bg-zinc-800 rounded-xl p-4 shadow-sm">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {getTypeLabel(type)}
                </div>
                <div className="mt-1 flex items-baseline justify-between">
                  <div className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {typeViews}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {percentage}%
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {typeStats.length} Einträge
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-sm overflow-hidden">
          <div className="divide-y divide-gray-200 dark:divide-zinc-700">
            {stats.sort((a, b) => b.views - a.views).map((stat, index) => {
              const name = getName(stat.entity_type, stat.entity_id);
              const details = getDetails(stat.entity_type, stat.entity_id);
              const percentage = ((stat.views / totalViews) * 100).toFixed(1);
              const typeStats = stats.filter(s => s.entity_type === stat.entity_type);
              const typeViews = typeStats.reduce((sum, s) => sum + s.views, 0);
              const typePercentage = ((stat.views / typeViews) * 100).toFixed(1);
              
              return (
                <div 
                  key={`${stat.entity_type}-${stat.entity_id}`}
                  className={`p-4 ${index % 2 === 0 ? 'bg-gray-50 dark:bg-zinc-900/50' : ''}`}
                >
                  <div className="flex flex-col gap-3">
                    {/* Hauptinformationen */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {name}
                          </span>
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-200 dark:bg-zinc-700 text-gray-600 dark:text-gray-300">
                            {getTypeLabel(stat.entity_type)}
                          </span>
                          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300">
                            {percentage}% der Gesamtviews
                          </span>
                          <span className="text-xs px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-300">
                            {typePercentage}% der {getTypeLabel(stat.entity_type)}-Views
                          </span>
                        </div>
                        {details && (
                          <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                            {details}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-sm font-medium text-blue-600 dark:text-blue-400 whitespace-nowrap">
                          {stat.views} Views
                        </div>
                        <div className="flex flex-col items-end">
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Platz #{index + 1}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {getTypeLabel(stat.entity_type)} #{typeStats.findIndex(s => s.entity_id === stat.entity_id) + 1}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Technische Details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <span>ID: {stat.entity_id}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        <span>Typ: {getTypeLabel(stat.entity_type)}</span>
                      </div>
                      {stat.ip_address && (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                          </svg>
                          <span>IP: {stat.ip_address}</span>
                        </div>
                      )}
                      {stat.user_agent && (
                        <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span className="truncate" title={stat.user_agent}>
                            {stat.user_agent}
                          </span>
                        </div>
                      )}
                      {stat.referrer && (
                        <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          <span className="truncate" title={stat.referrer}>
                            {stat.referrer}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
} 